import React, { useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import { FaBars, FaPhone, FaMapMarkerAlt, FaEnvelope, FaSignOutAlt } from "react-icons/fa";
import { getRestaurantProfile } from "../../utils/api";
import AuthContext, { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

import Settings from "../Settings";
import ReportsScreen from "./reportsScreen";
import AnalyticsScreen from "./analaticsScreen";

const API_URL = process.env.REACT_APP_API_BASE_URL_IMG;
       
export default function ProfileScreen() {
  const { restaurantId } = useContext(AuthContext);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState({
    name: "",
    address: "",
    phone: "",
    logoUrl: "",
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("profile");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (restaurantId) {
      getRestaurantProfile(restaurantId)
        .then((data) => {
          setProfile(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Failed to load profile", err);
          setLoading(false);
        });
    }
  }, [restaurantId]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rose-500"></div>
      </div>
    );

    return (
      <div className="w-full h-screen flex justify-center items-center bg-gradient-to-br from-rose-50 to-amber-50">
        {/* Mobile container */}
        <div className="relative w-[390px] h-[844px] bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="flex justify-between items-center h-16 px-4 border-b">
            <button onClick={() => setSidebarOpen(true)} className="text-2xl text-gray-700">
              <FaBars />
            </button>
            <h1 className="text-xl font-bold text-gray-800 capitalize">{activeTab}</h1>
          </div>
    
          {/* Sidebar */}
          <div
            className={`absolute top-0 left-0 h-full bg-white shadow-lg w-64 transform transition-transform duration-300 z-40 
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
          >
            <div className="p-6 space-y-6">
              <h2 className="text-lg font-semibold mb-4">Menu</h2>
              <button
                onClick={() => { setActiveTab("profile"); setSidebarOpen(false); }}
                className="block text-left text-gray-700 hover:text-rose-500"
              >
                Profile
              </button>
              <button
                onClick={() => { setActiveTab("reports"); setSidebarOpen(false); }}
                className="block text-left text-gray-700 hover:text-rose-500"
              >
                Reports
              </button>
              <button
                onClick={() => { setActiveTab("analytics"); setSidebarOpen(false); }}
                className="block text-left text-gray-700 hover:text-rose-500"
              >
                Analytics
              </button>
              <button
                onClick={() => { setActiveTab("settings"); setSidebarOpen(false); }}
                className="block text-left text-gray-700 hover:text-rose-500"
              >
                Settings
              </button>
              <button onClick={handleLogout} className="flex items-center gap-2 text-red-500 mt-6">
                <FaSignOutAlt /> Logout
              </button>
            </div>
          </div>
    
          {/* Overlay inside container */}
          {sidebarOpen && (
            <div
              className="absolute inset-0 bg-black bg-opacity-40 z-30"
              onClick={() => setSidebarOpen(false)}
            />
          )}
    
          {/* Main Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="h-[calc(100%-64px)] overflow-auto p-6"
          >
            {activeTab === "profile" && (
              <div className="text-center space-y-6">
                {/* Profile Image */}
                <div className="flex justify-center">
                  <img
                    src={profile.logoUrl ? `${API_URL}${profile.logoUrl}` : "https://via.placeholder.com/150"}
                    alt="Restaurant Logo"
                    className="w-28 h-28 rounded-full border-4 border-rose-200 object-cover"
                  />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{profile.name || "Your Restaurant"}</h2>
                  <p className="text-gray-500">Restaurant Profile</p>
                </div>
    
                {/* Info Card */}
                <div className="bg-rose-50 rounded-xl shadow-sm p-6 space-y-4 text-left">
                  <p><strong>Name:</strong> {profile.name || "Not set"}</p>
                  <p><strong>Address:</strong> {profile.address || "Not provided"}</p>
                  <p><strong>Phone:</strong> {profile.phone || "Not provided"}</p>
                </div>
    
                {/* Support Section */}
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Support Team</h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 text-gray-700">
                      <FaEnvelope className="text-rose-500" /> easidine@gmail.com
                    </div>
                    <div className="flex items-center gap-3 text-gray-700">
                      <FaPhone className="text-rose-500" /> +91 8073459731
                    </div>
                    <div className="flex items-center gap-3 text-gray-700">
                      <FaMapMarkerAlt className="text-rose-500" /> Bengaluru, Karnataka
                    </div>
                  </div>
                </div>
              </div>
            )}
            {activeTab === "analytics" && <AnalyticsScreen />}
            {activeTab === "reports" && <ReportsScreen />}
            {activeTab === "settings" && (
              <Settings profile={profile} setProfile={setProfile} />
            )}
          </motion.div>
        </div>
      </div>
    );
    
}
